import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useFormikContext } from "formik";
import AddButton from "../buttons/button";
import CloseIcon from "../../assets/icons/close-icon";
import { productViewAction } from "../../redux/actions/product-action";
import { useDispatch } from "react-redux";
import LoadingOverlay from "../helper-components/overlay-loading";
import IndianRupeeIcon from "../../assets/icons/indian-rupee";
import ItemsInputField from "../forms/items-input-field";
import DeleteIcon from "../../assets/icons/delete-icon";
import errorToast from "../helper-components/error-toast";
import ToggleSwitch from "../forms/toggle-switch";
import { useHotkeys } from "react-hotkeys-hook";
import React from "react";
import CouponPanel from "./coupon-panel";
import SchemePanel from "./scheme-panel";
import LoyaltyPanel from "./loyalty-panel";

export default function BillingPaymentPanel({
  open,
  setOpen,
  selectedItem,
  payModesList,
  handleSubmit = () => { },
  submitLoading = false,
  disabled = false,
  prefixName = "sot",
  setIsPrint = () => { }
}) {
  const [button, setButton] = useState("");
  const addRef = useRef(null);
  const submitRef = useRef(null);
  const printRef = useRef(null);

  const buttonRefs = useRef([]);
  if (buttonRefs.current?.length !== payModesList?.length) {
    buttonRefs.current = payModesList?.map(
      (_, i) => buttonRefs.current[i] || React.createRef()
    );
  }
  useHotkeys("a", () => {
    if (addRef.current) {
      addRef.current.click();
    }
  });
  useHotkeys("1,2,3,4,5,6,7,8,9", (event) => {
    const index = parseInt(event.key, 10) - 1;
    const button = buttonRefs.current[index];
    button?.current?.click();
  });
  useHotkeys("p", () => {
    if (printRef.current) {
      printRef.current.click();
    }
  });
  useHotkeys("s", () => {
    if (submitRef.current) {
      submitRef.current.click();
    }
  });
  const { setFieldValue } = useFormikContext();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { values } = useFormikContext();
  const [productDetails, setProductDetails] = useState({
    item_id: selectedItem?.itemid,
  });

  useEffect(() => {
    setProductDetails((prev) => ({
      ...prev,
      item_id: selectedItem?.itemid ?? "",
    }));
  }, [selectedItem]);

  useEffect(() => {
    if (productDetails.item_id) {
      setLoading(true);
      dispatch(productViewAction(productDetails.item_id, onSuccess, onError));
    }
  }, [productDetails.item_id, open]);

  const onSuccess = (data) => {
    setLoading(false);
    setProductDetails(data?.data?.[0]);
  };

  const onError = () => {
    setLoading(false);
  };

  const [currentPaymentMethod, setcurrentPaymentMethod] = useState("");

  // For Cash, the amount actually settled toward the bill can't exceed the
  // cash physically received. If Received Cash is entered and is less than the
  // Cash amount, only the received cash counts as paid.
  const getEffectivePaid = (p) => {
    let paid = parseFloat(p?.payment_amount || 0);
    if (p?.payment_mode_name === "Cash") {
      const hasReceived =
        p?.received_amount !== null &&
        p?.received_amount !== undefined &&
        p?.received_amount !== "";
      const received = parseFloat(p?.received_amount || 0);
      if (hasReceived && received < paid) {
        paid = received;
      }
    }
    return paid;
  };

  const totalPayment =
    values?.paymentData?.reduce((acc, curr) => acc + getEffectivePaid(curr), 0) ||
    0;

  const remaining_amount = (
    parseFloat(values?.[`${prefixName}_total_amount`] || 0) -
    parseFloat(totalPayment || 0)
  ).toFixed(2);

  // Overpayment (negative remaining) or exact-match failure should block save.
  // The Remaining amount already accounts for short cash via getEffectivePaid
  // (a cash shortfall only counts the received amount), so when other modes
  // cover the gap and Remaining is 0 the bill is fully settled and Save works.
  const isAmountMismatch = parseFloat(remaining_amount) !== 0;

  const isSaveBlocked = isAmountMismatch;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={() => {
          setOpen(false);
        }}
      >
        <div className="fixed inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <Dialog.Panel className="h-full relative w-[400px] sm:w-[600px] bg-white overflow-y-auto rounded-tl-lg rounded-bl-lg shadow-xl">
                {/* Header */}
                <div
                  className="h-[61px] bg-[var(--slide-panel-head-bg)] px-3 flex items-center justify-between border-b"
                  style={{ borderColor: "var(--slide-panel-border)" }}
                >
                  <div
                    style={{
                      maxHeight: "32px",
                      color: "var(--slide-panel-h-fg)",
                      fontSize: "var(--slide-panel-h-fs)",
                      fontWeight: "var(--slide-panel-h-fw)",
                    }}
                    className="min-w-[75%] "
                  >
                    Payment
                  </div>
                  <AddButton
                    onclkFn={() => {
                      setOpen(false);
                    }}
                    icon={
                      <CloseIcon
                        width="12px"
                        height="12px"
                        fill="var(--close-button-fg)"
                      />
                    }
                    text={
                      <span>
                        Close <span className="opacity-50">(X)</span>{" "}
                      </span>
                    }
                    className="flex h-[32px]  items-center gap-2 bg-[var(--close-button-bg)] border-radius px-3 text-[var(--close-button-fg)]"
                  />
                </div>

                <div
                  style={{
                    height: `calc(100% - 61px)`,
                    minHeight: `calc(100% - 61px)`,
                  }}
                  className="px-3 pb-[5rem] relative overflow-y-auto mt-4"
                >
                  {/* Benefit panels are sales-only. Return-bill flow reuses
                      this panel with prefixName="icn", but the return submit
                      doesn't ship a benefits payload (and the backend has no
                      return-side audit handling), so rendering them there
                      would silently mutate icn_total_amount without any
                      matching coupon_usage / bill_offer_application row. */}
                  {prefixName === "sot" && (
                    <>
                      <SchemePanel
                        cmrId={values?.cmr_id}
                        phoneE164={values?.cmr_phone_number || null}
                        storeId={values?.store_id || localStorage.getItem("warehouse_id")}
                        cashierId={values?.created_by || null}
                        billTotal={Number(values?.[`${prefixName}_total_amount`] || 0)}
                        billRef={values?.[`${prefixName}_id`] || null}
                        setFieldValue={setFieldValue}
                        values={values}
                        prefixName={prefixName}
                      />
                      <CouponPanel
                        cmrId={values?.cmr_id}
                        phoneE164={values?.cmr_phone_number || null}
                        storeId={values?.store_id || localStorage.getItem("warehouse_id")}
                        cashierId={values?.created_by || null}
                        billTotal={Number(values?.[`${prefixName}_total_amount`] || 0)}
                        billRef={values?.[`${prefixName}_id`] || null}
                        paymentData={values?.paymentData}
                        setFieldValue={setFieldValue}
                        values={values}
                        prefixName={prefixName}
                      />
                      <LoyaltyPanel
                        cmrId={values?.cmr_id}
                        phoneE164={values?.cmr_phone_number || null}
                        storeId={values?.store_id || localStorage.getItem("warehouse_id")}
                        cashierId={values?.created_by || null}
                        billTotal={Number(values?.[`${prefixName}_total_amount`] || 0)}
                        billRef={values?.[`${prefixName}_id`] || null}
                        paymentData={values?.paymentData}
                        setFieldValue={setFieldValue}
                        values={values}
                        prefixName={prefixName}
                      />
                    </>
                  )}
                  <div className="h-[5.12rem] w-full flex items-center">
                    <div className="bg-[var(--net-payable-con-bg)] min-h-[52px] flex items-center px-4 justify-between rounded-[8px] w-full">
                      <span className="text-[var(--net-payable-text-color)] text-[length:var(--net-payable-fs)] font-[var(--net-payable-label-fw)]">
                        Net Payable amount
                      </span>
                      <span className="text-[var(--net-payable-text-color)] text-[length:var(--net-payable-fs)] font-[var(--net-payable-value-fw)]">
                        ₹ {values?.[`${prefixName}_total_amount`]}
                      </span>
                    </div>
                  </div>
                  <div className="border border-[var(--slide-panel-border)] rounded min-h-[10rem]">
                    <div className="h-full overflow-auto">
                      <div className="flex flex-wrap gap-x-4 gap-y-4 p-4">
                        {payModesList?.map((type, index) => (
                          <button
                            ref={buttonRefs.current[index]}
                            key={index}
                            disabled={values?.paymentData?.some(
                              (p) => p?.payment_mode_name === type?.value
                            )}
                            onClick={() => {
                              if (type?.value === "Cash") {
                                const updatedData = [
                                  ...(values?.paymentData ?? []),
                                ];
                                setFieldValue("paymentData", [
                                  ...updatedData,
                                  {
                                    payment_mode_name: "Cash",
                                    payment_amount: remaining_amount,
                                    received_amount: null,
                                    tender_amount: null,
                                  },
                                ]);
                              } else if (type?.value === "CB") {
                                const updatedData = [
                                  ...(values?.paymentData ?? []),
                                ];
                                setFieldValue("paymentData", [
                                  ...updatedData,
                                  {
                                    payment_mode_name: type?.value,
                                    payment_amount:
                                      values?.cmr_credit_balance || 0,
                                    transaction_id: null,
                                  },
                                ]);
                              } else {
                                const updatedData = [
                                  ...(values?.paymentData ?? []),
                                ];
                                setFieldValue("paymentData", [
                                  ...updatedData,
                                  {
                                    payment_mode_name: type?.value,
                                    payment_amount: remaining_amount,
                                    transaction_id: null,
                                  },
                                ]);
                              }
                            }}
                            className={`
            ${values?.paymentData?.some(
                              (p) => p?.payment_mode_name === type?.value
                            )
                                ? "text-[var(--pay-type-con-disabled-fg)] bg-[var(--pay-type-con-bg)] cursor-not-allowed"
                                : currentPaymentMethod === type?.value
                                  ? "text-[var(--pay-type-selec-fg)] bg-[var(--pay-type-selec-bg)]"
                                  : "text-[var(--pay-type-con-fg)] bg-[var(--pay-type-con-bg)]"
                              }
            flex items-center justify-between gap-2 px-4 py-2 h-[44px]
            basis-[calc(33.333%-0.666rem)] max-w-[calc(33.333%-0.666rem)] rounded
            text-[length:var(--pay-type-con-fs)] font-[var(--pay-type-con-fw)]
            flex-grow-0 self-start whitespace-nowrap
          `}
                          >
                            <span className="flex items-center gap-2">
                              <IndianRupeeIcon
                                fill={
                                  values?.paymentData?.some(
                                    (p) => p?.payment_mode_name === type?.value
                                  )
                                    ? "var(--pay-type-con-disabled-fg)"
                                    : currentPaymentMethod === type?.value
                                      ? "var(--pay-type-selec-fg)"
                                      : "black"
                                }
                              />
                              {type?.label}
                            </span>
                            <span>({index + 1})</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Payment Summary outer card. The reserved height is tuned
                      against the rest of the side panel (CMR header / loyalty
                      / coupon / scheme / payment methods). The base allocation
                      assumed 2 rows (Total Payable + Remaining). Once a scheme
                      or coupon discount is applied we render an Original
                      Amount row, the discount row(s), and possibly a Paid-so-
                      far row — up to 4 extra rows that push the card content
                      past its fixed height and clip Remaining. Grow the
                      allocation when those rows are present. */}
                  {(() => {
                    const hasBreakdown =
                      !!(values?.schemeDiscount || values?.couponDiscount || values?.pointsDiscount);
                    const baseRem = values?.cmr_id ? 33 : 26;
                    // Sign was flipped — adding to the allocation REDUCED the
                    // Payment Summary card (height: calc(100% - allocatedRem)).
                    // When a discount breakdown is present we need MORE room
                    // for the extra rows, so shrink the allocation by ~3rem;
                    // otherwise keep the historical baseline.
                    // Grow Payment Summary by ~5rem when the breakdown is
                    // showing — up to 4 extra rows are possible (Original +
                    // Scheme + Coupon + Loyalty), so allocate generously.
                    const allocatedRem = baseRem - (hasBreakdown ? 5 : 0);
                    return (
                      <div
                        style={{
                          // minHeight: `calc(100% - ${allocatedRem}rem)`,
                          // height: `calc(100% - ${allocatedRem}rem)`,
                          height: `57%`,
                        }}
                        className="border border-[var(--slide-panel-border)]  border-radius mt-[1rem] "
                      >
                        <div className="bg-[var(--payment-summary-head-bg)] text-[var(--payment-summary-head-fg)] text-[length:var(--payment-summary-head-fs)] font-[var(--payment-summary-head-fw)] w-full h-[52px] flex items-center px-4 border-radius">
                          Payment Summary
                        </div>
                        <div
                          style={{
                            // Reserve 173px for the card header + bottom borders;
                            // the inner scroll fills whatever the parent grew to.
                            // With a discount breakdown present the parent grew
                            // (sibling allocation above) so this calc keeps the
                            // box's full content visible without forcing a scroll
                            // that hides the Remaining row.
                            // minHeight: `calc(100% - 173px)`,
                            // maxHeight: `calc(100% - 60px)`,
                          }}
                          className="flex flex-col gap-3 p-4 overflow-y-auto"
                        >
                          {values?.paymentData?.map((pay, index) => (
                            <div className="flex gap-3 items-center">
                              <div
                                className={`${pay?.payment_mode_name === "Credit" ||
                                  pay?.payment_mode_name === "CB"
                                  ? "w-[95%]"
                                  : "w-[29%]"
                                  }`}
                              >
                                <ItemsInputField
                                  type="number"
                                  disabled={pay?.payment_mode_name === "CB"}
                                  name={`paymentData[${index}].payment_amount`} // Dynamic name using Formik's field structure
                                  label={pay?.payment_mode_name}
                                  isLabelReq={true}
                                  labelClass={
                                    "min-h-[1.2rem] text-[var(--pay-met-lab-color)] text-[length:var(--pay-met-lab-fs)] font-[var(--pay-met-lab-fw)]"
                                  }
                                  inputClass={
                                    "min-h-[40px] px-2 border border-[var(--pay-met-input-border)] focus:outline-none bg-[var(--pay-met-input-bg)] text-[var(--pay-met-val-color)] text-[length:var(--pay-met-val-fs)] font-[var(--pay-met-val-fw)] border-radius input-field-no-spin-buttons"
                                  }
                                  onChange={(e) => {
                                    const netPayable = parseFloat(
                                      values?.[`${prefixName}_total_amount`] || 0
                                    );
                                    const otherTotal = (
                                      values?.paymentData ?? []
                                    ).reduce(
                                      (acc, p, i) =>
                                        i === index ? acc : acc + getEffectivePaid(p),
                                      0
                                    );
                                    const maxForThisRow = Math.max(
                                      netPayable - otherTotal,
                                      0
                                    );
                                    let amount = parseFloat(e?.target?.value || 0);
                                    // Clamp so the combined payments never exceed Net Payable.
                                    if (amount > maxForThisRow) {
                                      amount = maxForThisRow;
                                      errorToast(
                                        "Amount cannot exceed the net payable amount"
                                      );
                                      setFieldValue(
                                        `paymentData[${index}].payment_amount`,
                                        amount
                                      );
                                    }
                                    const received = parseFloat(
                                      pay?.received_amount || 0
                                    );
                                    setFieldValue(
                                      `paymentData[${index}].tender_change`,
                                      received - amount > 0 ? received - amount : 0
                                    );
                                  }}
                                />
                              </div>
                              {pay?.payment_mode_name === "Cash" && (
                                <>
                                  <div className="w-[29%]">
                                    <ItemsInputField
                                      type="number"
                                      className
                                      name={`paymentData[${index}].received_amount`} // Dynamic name using Formik's field structure
                                      label={"Received Cash"}
                                      isLabelReq={true}
                                      labelClass={
                                        "min-h-[1.2rem] text-[var(--pay-met-lab-color)] text-[length:var(--pay-met-lab-fs)] font-[var(--pay-met-lab-fw)]"
                                      }
                                      inputClass={
                                        "min-h-[40px] px-2 border border-[var(--pay-met-input-border)] focus:outline-none bg-[var(--pay-met-input-bg)] text-[var(--pay-met-val-color)] text-[length:var(--pay-met-val-fs)] font-[var(--pay-met-val-fw)] border-radius input-field-no-spin-buttons"
                                      }
                                      onChange={(e) => {
                                        setFieldValue(
                                          `paymentData[${index}].tender_change`,
                                          parseFloat(e?.target?.value) -
                                            parseFloat(pay?.payment_amount) >
                                            0
                                            ? parseFloat(e?.target?.value) -
                                            parseFloat(pay?.payment_amount)
                                            : 0
                                        );
                                      }}
                                    />
                                  </div>
                                  <div className="w-[29%]">
                                    <ItemsInputField
                                      type="number"
                                      disabled={true}
                                      className
                                      name={`paymentData[${index}].tender_change`} // Dynamic name using Formik's field structure
                                      label={"Tender Change"}
                                      isLabelReq={true}
                                      labelClass={
                                        "min-h-[1.2rem] text-[var(--pay-met-lab-color)] text-[length:var(--pay-met-lab-fs)] font-[var(--pay-met-lab-fw)]"
                                      }
                                      inputClass={
                                        "min-h-[40px] px-2 border border-[var(--pay-met-input-border)] focus:outline-none bg-[var(--pay-met-input-bg)] text-[var(--pay-met-val-color)] text-[length:var(--pay-met-val-fs)] font-[var(--pay-met-val-fw)] border-radius input-field-no-spin-buttons"
                                      }
                                    />
                                  </div>
                                </>
                              )}

                              {pay?.payment_mode_name !== "Cash" &&
                                pay?.payment_mode_name !== "CB" &&
                                pay?.payment_mode_name !== "Credit" && (
                                  <div className="w-[60.5%]">
                                    <ItemsInputField
                                      className
                                      name={`paymentData[${index}].transaction_id`} // Dynamic name using Formik's field structure
                                      label={"Transaction Id"}
                                      isLabelReq={true}
                                      labelClass={
                                        "min-h-[1.2rem] text-[var(--pay-met-lab-color)] text-[length:var(--pay-met-lab-fs)] font-[var(--pay-met-lab-fw)]"
                                      }
                                      inputClass={
                                        "min-h-[40px] px-2 border border-[var(--pay-met-input-border)] focus:outline-none bg-[var(--pay-met-input-bg)] text-[var(--pay-met-val-color)] text-[length:var(--pay-met-val-fs)] font-[var(--pay-met-val-fw)] border-radius"
                                      }
                                    />
                                  </div>
                                )}

                              {!disabled && (
                                <button
                                  onClick={() => {
                                    let updatedData = [...values.paymentData];
                                    updatedData.splice(index, 1);
                                    setFieldValue("paymentData", updatedData);
                                  }}
                                  className="mt-4 bg-[var(--pay-type-del-bg)] p-2 border-radius "
                                >
                                  <DeleteIcon />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>

                        <div className="px-4 h-[84%] flex flex-col gap-[12px] justify-between">
                          <div className="h-[38px]">
                            {currentPaymentMethod && !disabled && (
                              <AddButton
                                ref={addRef}
                                type="button"
                                onclkFn={() => {
                                  if (currentPaymentMethod === "Cash") {
                                    const updatedData = [
                                      ...(values?.paymentData ?? []),
                                    ];
                                    setFieldValue("paymentData", [
                                      ...updatedData,
                                      {
                                        payment_mode_name: "Cash",
                                        payment_amount:
                                          values?.paymentData?.length === 0
                                            ? values?.[`${prefixName}_total_amount`]
                                            : 0,
                                        received_amount: null,
                                        tender_amount: null,
                                      },
                                    ]);
                                  } else if (currentPaymentMethod === "CB") {
                                    const updatedData = [
                                      ...(values?.paymentData ?? []),
                                    ];
                                    setFieldValue("paymentData", [
                                      ...updatedData,
                                      {
                                        payment_mode_name: currentPaymentMethod,
                                        payment_amount:
                                          values?.cmr_credit_balance || 0,
                                        transaction_id: null,
                                      },
                                    ]);
                                  } else {
                                    const updatedData = [
                                      ...(values?.paymentData ?? []),
                                    ];
                                    setFieldValue("paymentData", [
                                      ...updatedData,
                                      {
                                        payment_mode_name: currentPaymentMethod,
                                        payment_amount:
                                          values?.paymentData?.length === 0
                                            ? values?.[`${prefixName}_total_amount`]
                                            : 0,
                                        transaction_id: null,
                                      },
                                    ]);
                                  }
                                  setcurrentPaymentMethod("");
                                }}
                                prefixIcon={false}
                                text={
                                  <>
                                    Add Payment Method{" "}
                                    <span className="opacity-50"> (A)</span>
                                  </>
                                }
                                className="flex items-center   h-[38px] whitespace-nowrap border-radius 
                 bg-[var(--add-pay-met-btn-bg)] 
                 cursor-pointer 
                 text-[var(--add-pay-met-btn-fg)] 
                 text-[length:var(--add-pay-met-btn-fs)] 
                 font-[var(--create-table-clearAll-fw)] w-full justify-center gap-3"
                              />
                            )}
                          </div>
                          <div className="border border-[var(--slide-panel-border)] p-2 flex flex-col border-radius">
                            {/* Discount breakdown — shown only when a coupon or
                            scheme is applied. Original amount + each discount
                            with its source label so the cashier can read the
                            chain at a glance. */}
                            {(values?.schemeDiscount || values?.couponDiscount || values?.pointsDiscount) && (
                              <>
                                <div className="flex justify-between px-3 pt-2 pb-1">
                                  <span className="text-[12px] text-[#64748B]">
                                    Original Amount
                                  </span>
                                  <span className="text-[12px] text-[#64748B] tabular-nums">
                                    ₹{" "}
                                    {parseFloat(
                                      values?.[`${prefixName}_original_total_amount`] ??
                                      values?.[`${prefixName}_total_amount`] ??
                                      0
                                    ).toFixed(2)}
                                  </span>
                                </div>
                                {values?.schemeDiscount && (
                                  <div className="flex justify-between px-3 py-1">
                                    <span className="text-[12px] text-[#15803C] font-semibold flex items-center gap-1.5">
                                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#15803C]" />
                                      Scheme
                                      <span className="text-[#64748B] font-normal truncate max-w-[180px]">
                                        · {values.schemeDiscount.scheme_name || "applied"}
                                      </span>
                                    </span>
                                    <span className="text-[12px] text-[#15803C] font-bold tabular-nums">
                                      − ₹{" "}
                                      {parseFloat(values.schemeDiscount.discount_amount || 0).toFixed(2)}
                                    </span>
                                  </div>
                                )}
                                {values?.couponDiscount && (
                                  <div className="flex justify-between px-3 py-1">
                                    <span className="text-[12px] text-[#7C3AED] font-semibold flex items-center gap-1.5">
                                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#7C3AED]" />
                                      Coupon
                                      <span className="text-[#64748B] font-normal truncate max-w-[180px]">
                                        · {values.couponDiscount.coupon_code || values.couponDiscount.coupon_name || "applied"}
                                      </span>
                                    </span>
                                    <span className="text-[12px] text-[#7C3AED] font-bold tabular-nums">
                                      − ₹{" "}
                                      {parseFloat(values.couponDiscount.discount_amount || 0).toFixed(2)}
                                    </span>
                                  </div>
                                )}
                                {values?.pointsDiscount && (
                                  <div className="flex justify-between px-3 py-1">
                                    <span className="text-[12px] text-[#B7791F] font-semibold flex items-center gap-1.5">
                                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#B7791F]" />
                                      Loyalty
                                      <span className="text-[#64748B] font-normal truncate max-w-[200px]">
                                        · {parseFloat(values.pointsDiscount.points || 0).toFixed(0)} pts × ₹{parseFloat(values.pointsDiscount.pt_value || 0).toFixed(2)}/pt
                                      </span>
                                    </span>
                                    <span className="text-[12px] text-[#B7791F] font-bold tabular-nums">
                                      − ₹{" "}
                                      {parseFloat(values.pointsDiscount.discount_amount || 0).toFixed(2)}
                                    </span>
                                  </div>
                                )}
                                <div className="border-b border-b-[var(--slide-panel-border)]" />
                              </>
                            )}

                            <div className="flex justify-between border-b border-b-[var(--slide-panel-border)] p-3">
                              <span className="text-[var(--payment-summary-label-fg)] text-[length:var(--payment-summary-label-fs)] font-[var(--payment-summary-label-fw)]">
                                Total Payable Amount
                              </span>
                              <span className="text-[var(--payment-summary-value-fg)] text-[length:var(--payment-summary-value-fs)] font-[var(--payment-summary-value-fw)]">
                                ₹ {parseFloat(values?.[`${prefixName}_total_amount`] || 0).toFixed(2)}
                              </span>
                            </div>
                            {parseFloat(totalPayment || 0) > 0 && (
                              <div className="flex justify-between border-b border-b-[var(--slide-panel-border)] px-3 py-2">
                                <span className="text-[12px] text-[#64748B]">
                                  Paid so far
                                </span>
                                <span className="text-[12px] text-[#64748B] tabular-nums">
                                  ₹ {parseFloat(totalPayment).toFixed(2)}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between p-3">
                              <span className="text-[var(--payment-summary-label-fg)] text-[length:var(--payment-summary-label-fs)] font-[var(--payment-summary-label-fw)]">
                                Remaining Amount
                              </span>
                              <span
                                className={`${isAmountMismatch
                                  ? "text-[#F94B4B]"
                                  : "text-[var(--payment-summary-value-fg)]"
                                  } text-[length:var(--payment-summary-value-fs)] font-[var(--payment-summary-value-fw)]`}
                              >
                                ₹ {remaining_amount}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <LoadingOverlay isLoading={loading} />
                </div>

                <LoadingOverlay isLoading={submitLoading} />
                <div className="h-[5rem]  absolute bottom-0  flex justify-between items-center px-3 w-full">
                  {prefixName === "sot" ? (
                    <ToggleSwitch
                      disabled={disabled}
                      onClick={() => {
                        setFieldValue(
                          `${prefixName}_is_store_pick_up`,
                          !values?.[`${prefixName}_is_store_pick_up`]
                        );
                      }}
                      checked={values?.[`${prefixName}_is_store_pick_up`]}
                      secondaryText="Delivery"
                      primaryText="Store Pickup"
                    />
                  ) : (
                    <div></div>
                  )}

                  {!disabled && (
                    <div className="flex gap-2">
                      <AddButton
                        ref={submitRef}
                        isDisabled={submitLoading || isSaveBlocked}
                        loading={submitLoading && button === "submit"}
                        onclkFn={() => {
                          if (submitLoading) return;
                          if (isAmountMismatch) {
                            errorToast("Amount is not Matching");
                            return;
                          }
                          setFieldValue("submittedButton", "submit");
                          handleSubmit();
                          setButton("submit");
                          setIsPrint(false);
                        }}
                        prefixIcon={false}
                        text={
                          <span>
                            Save <span className="opacity-50">(s)</span>{" "}
                          </span>
                        }
                        className="
    flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
    bg-[var(--primary-button-bg-color)] cursor-pointer
    text-[var(--primary-button-fg-color)]
    border border-[var(--primary-button-bg-color)]         
    hover:bg-[var(--primary-button-hover-color)]
    text-[length:var(--primary-button-font-size)] 
    font-[var(--primary-button-font-weight)]
  "
                        type="submit"
                      />
                      <AddButton
                        ref={printRef}
                        isDisabled={submitLoading || isSaveBlocked}
                        loading={submitLoading && button === "print"}
                        onclkFn={() => {
                          if (submitLoading) return;
                          if (isAmountMismatch) {
                            errorToast("Amount is not Matching");
                            return;
                          }
                          setFieldValue("submittedButton", "print");
                          handleSubmit();
                          setButton("print");
                          setIsPrint(true);
                        }}
                        prefixIcon={false}
                        text={
                          <span>
                            Save and Print{" "}
                            <span className="opacity-50">(P)</span>{" "}
                          </span>
                        }
                        className="
    flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
    bg-[var(--primary-button-bg-color)] cursor-pointer
    text-[var(--primary-button-fg-color)]
    border border-[var(--primary-button-bg-color)]
    hover:bg-[var(--primary-button-hover-color)]
    text-[length:var(--primary-button-font-size)] 
    font-[var(--primary-button-font-weight)]
  "
                        type="submit"
                      />
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
